// ─────────────────────────────────────────────────────────────────
// Glass-i — UOM Master grid columns (Stage 6B.8A-1).
//
// Flat single-row layout, server-mode. Mirrors supplierColumns shape
// but trimmed for the UOM essentials.
// ─────────────────────────────────────────────────────────────────

import { Tag } from 'antd';
import {
  LockOutlined, CheckCircleTwoTone, StopOutlined
} from '@ant-design/icons';
import {
  MEASUREMENT_TYPE_OPTIONS, MEASUREMENT_TYPE_COLOR
} from './uomConstants';

const MEASUREMENT_TYPE_LABEL = Object.fromEntries(
  MEASUREMENT_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

export function buildUomColumns({ currentPage, pageSize } = {}) {
  const sNo = {
    title: '#',
    key: '__sno__',
    width: 56,
    align: 'center',
    fixed: 'left',
    render: (_v, _r, idx) => {
      const cp = Number(currentPage) > 0 ? Number(currentPage) : 1;
      const ps = Number(pageSize)    > 0 ? Number(pageSize)    : 25;
      return <span className="erp-grid__muted">{(cp - 1) * ps + idx + 1}</span>;
    }
  };

  const uomCode = {
    title: 'UOM Code',
    key: 'uomCode',
    dataIndex: 'uomCode',
    sortField: 'uom_code',
    sorter: true,
    width: 130,
    fixed: 'left',
    dataType: 'string',
    render: (v) => v
      ? <code className="erp-grid__code">{v}</code>
      : <span className="erp-grid__muted">—</span>
  };

  const uomName = {
    title: 'UOM Name',
    key: 'uomName',
    dataIndex: 'uomName',
    sortField: 'uom_name',
    sorter: true,
    width: 220,
    ellipsis: true,
    dataType: 'string',
    render: (v) => <span className="erp-grid__primary">{v}</span>
  };

  const uomSymbol = {
    title: 'Symbol',
    key: 'uomSymbol',
    dataIndex: 'uomSymbol',
    width: 100,
    align: 'center',
    dataType: 'string',
    render: (v) => v
      ? <span style={{ fontWeight: 500 }}>{v}</span>
      : <span className="erp-grid__muted">—</span>
  };

  const uqcCode = {
    title: 'GST UQC',
    key: 'uqcCode',
    dataIndex: 'uqcCode',
    sortField: 'uqc_code',
    sorter: true,
    width: 120,
    dataType: 'string',
    render: (v, r) => v
      ? (
        <span>
          <code className="erp-grid__code">{v}</code>
          {r && r.isGstStandard === false ? (
            <span style={{ marginLeft: 6, color: '#d97706', fontSize: 11 }} title="Not a direct GSTN UQC — confirm reporting treatment">
              ⚠
            </span>
          ) : null}
        </span>
      )
      : <span className="erp-grid__muted">—</span>
  };

  const uqcName = {
    title: 'UQC Name',
    key: 'uqcName',
    dataIndex: 'uqcName',
    width: 180,
    ellipsis: true,
    dataType: 'string',
    hidden: true,
    render: (v) => v
      ? <span>{v}</span>
      : <span className="erp-grid__muted">—</span>
  };

  const measurementType = {
    title: 'Measurement',
    key: 'measurementType',
    dataIndex: 'measurementType',
    sortField: 'measurement_type',
    sorter: true,
    filterField: 'measurement_type',
    width: 140,
    dataType: 'enum',
    filters: MEASUREMENT_TYPE_OPTIONS.map((o) => ({ text: o.label, value: o.value })),
    render: (v) => v
      ? (
        <Tag color={MEASUREMENT_TYPE_COLOR[v] || 'default'} className="erp-grid__tag">
          {MEASUREMENT_TYPE_LABEL[v] || v}
        </Tag>
      )
      : <span className="erp-grid__muted">—</span>
  };

  const decimalPrecision = {
    title: 'Decimals',
    key: 'decimalPrecision',
    dataIndex: 'decimalPrecision',
    sortField: 'decimal_precision',
    sorter: true,
    width: 100,
    align: 'center',
    dataType: 'number',
    render: (v) => v != null ? <span>{Number(v)}</span> : <span className="erp-grid__muted">—</span>
  };

  const isSystem = {
    title: 'System',
    key: 'isSystem',
    dataIndex: 'isSystem',
    sortField: 'is_system',
    sorter: true,
    filterField: 'is_system',
    width: 100,
    align: 'center',
    dataType: 'boolean',
    filters: [
      { text: 'System', value: 'true' },
      { text: 'User',   value: 'false' }
    ],
    render: (v) => v
      ? <Tag icon={<LockOutlined />} color="default" className="erp-grid__tag">System</Tag>
      : <span className="erp-grid__muted">User</span>
  };

  const isActive = {
    title: 'Status',
    key: 'isActive',
    dataIndex: 'isActive',
    sortField: 'is_active',
    sorter: true,
    filterField: 'is_active',
    width: 110,
    align: 'center',
    dataType: 'boolean',
    filters: [
      { text: 'Active',   value: 'true' },
      { text: 'Inactive', value: 'false' }
    ],
    render: (v) => v
      ? <Tag icon={<CheckCircleTwoTone twoToneColor="#52c41a" />} color="success" className="erp-grid__tag">Active</Tag>
      : <Tag icon={<StopOutlined />} color="default" className="erp-grid__tag">Inactive</Tag>
  };

  return [
    sNo,
    uomCode, uomName, uomSymbol,
    uqcCode, uqcName,
    measurementType, decimalPrecision,
    isSystem, isActive
  ];
}
